"use client";

import React from "react";
import { useCopyToClipboard } from "react-use";

import Link from "next/link";

import { PopoverContent } from "@radix-ui/react-popover";
import tokens from "most-design-system/tokens/js";

import { Popover, PopoverTrigger } from "components/Popover";
import Video from "components/Video";

import { cn } from "lib/cn";

import FacebookBwIcon from "/icons/facebook-logo-bw.svg";
import InstagramBwIcon from "/icons/instagram-logo-bw.svg";
import LinkedinBwIcon from "/icons/linkedin-logo-bw.svg";
import XBwIcon from "/icons/x-logo-bw.svg";
import AppStoreBadgeImage from "/images/common/app-store-badge.svg";
import MoreLogoImage from "/images/common/more-logo.svg";
import PlayStoreBadgeImage from "/images/common/play-store-badge.svg";

import ArrowOutwardIcon from "@material-symbols/svg-700/sharp/arrow_outward.svg";
import ContentCopyIcon from "@material-symbols/svg-700/sharp/content_copy.svg";

const emailAddress = "hello@more.in";

const socials = [
  {
    name: "Instagram",
    icon: InstagramBwIcon,
    href: "https://www.instagram.com/moreretail",
  },
  {
    name: "Facebook",
    icon: FacebookBwIcon,
    href: "https://www.facebook.com/MoreRetailStores/",
  },
  {
    name: "LinkedIn",
    icon: LinkedinBwIcon,
    href: "https://www.linkedin.com/company/more-retail-private-limited/",
  },
  {
    name: "X",
    icon: XBwIcon,
    href: "https://twitter.com/moreretail",
  },
];

interface Link {
  name: string;
  href: string;
  target?: "_blank" | "_self";
}

const featuredLinks: Link[] = [
  {
    name: "Find A Store",
    href: "/find-a-store",
  },
  {
    name: "cookbook",
    href: "/cookbook",
  },
  {
    name: "Investors",
    href: "/investors",
  },
];

const aboutLinks: Link[] = [
  {
    name: "History",
    href: "/history",
  },
  {
    name: "Leadership",
    href: "/leadership",
  },
  {
    name: "Values",
    href: "/values",
  },
];

const otherLinks: Link[] = [
  {
    name: "Careers",
    href: "/careers",
  },
  {
    name: "FAQs",
    href: "/faq",
  },
];

const legalLinks: Link[] = [
  {
    name: "Terms",
    href: "/terms",
  },
  {
    name: "Policies",
    href: "/policy",
  },
  {
    name: "Privacy",
    href: "/privacy",
  },
];

interface FooterProps {
  background?: { type: "color" | "video"; hex?: string; src?: string };
  className?: string;
}

function Footer({
  background = {
    type: "video",
    src: "/videos/common/sholinganallur-fpv/sholinganallur-fpv.m3u8",
  },
  className = "",
}: FooterProps) {
  const [_, copyToClipboard] = useCopyToClipboard();

  return (
    <footer
      className={cn(
        "pointer-events-auto relative -mt-8 flex w-full flex-col items-center justify-center overflow-hidden bg-black",
        className,
      )}
    >
      <div className="z-[1] scale-25 pt-40 sm:scale-45 md:scale-60 lg:scale-75 xl:scale-90 2xl:scale-95 3xl:scale-100 4xl:scale-120">
        <div
          className="h-128 w-328 transform-gpu bg-white/15 backdrop-blur-3xl"
          style={{ clipPath: "url(#moreLogoClipPath)" }}
        ></div>
        <svg xmlns="http://www.w3.org/2000/svg">
          <defs>
            {/* This is the more logo combined into a single compound path with Adobe Illustrator and then converted to a relative numbering system (objectBoundingBox) with an online tool */}
            <clipPath id="moreLogoClipPath" clipPathUnits="objectBoundingBox">
              <path d="M0.489,0.5 c-0.012,0,-0.018,0.009,-0.025,0.03 s-0.008,0.043,-0.008,0.073 s0.003,0.051,0.008,0.073 s0.013,0.03,0.025,0.03 s0.018,-0.009,0.025,-0.03 c0.005,-0.021,0.008,-0.043,0.008,-0.073 s-0.003,-0.051,-0.008,-0.073 c-0.005,-0.021,-0.013,-0.03,-0.025,-0.03 M0.819,0.5 c-0.005,-0.013,-0.012,-0.021,-0.02,-0.021 s-0.017,0.009,-0.02,0.021 c-0.005,0.013,-0.007,0.03,-0.008,0.047 h0.053 c0,-0.017,-0.002,-0.034,-0.007,-0.047 h0 M0.499,0.013 C0.208,0.013,0,0.303,0,0.303 l0.08,0.709 h0.84 l0.08,-0.709 S0.79,0.013,0.499,0.013 M0.386,0.812 h-0.06 v-0.248 c0,-0.038,-0.005,-0.056,-0.017,-0.056 s-0.018,0.017,-0.018,0.056 v0.248 h-0.06 v-0.248 c0,-0.038,-0.005,-0.056,-0.017,-0.056 s-0.018,0.017,-0.018,0.056 v0.248 h-0.06 v-0.419 h0.047 l0.007,0.056 h0 c0.005,-0.021,0.01,-0.038,0.018,-0.051 s0.018,-0.017,0.03,-0.017 s0.022,0.004,0.028,0.017 c0.008,0.013,0.013,0.03,0.017,0.051 h0 c0.005,-0.021,0.012,-0.038,0.018,-0.051 c0.008,-0.013,0.018,-0.017,0.03,-0.017 s0.017,0,0.023,0.013 c0.007,0.009,0.012,0.017,0.017,0.03 s0.007,0.026,0.01,0.043 c0.002,0.017,0.003,0.034,0.003,0.056 v0.291 M0.581,0.611 c0,0.034,-0.003,0.064,-0.008,0.09 c-0.005,0.026,-0.012,0.047,-0.02,0.068 c-0.008,0.017,-0.018,0.034,-0.028,0.043 c-0.012,0.009,-0.022,0.013,-0.035,0.013 s-0.023,-0.004,-0.035,-0.013 c-0.012,-0.009,-0.02,-0.021,-0.028,-0.043 c-0.008,-0.017,-0.015,-0.043,-0.02,-0.068 c-0.005,-0.026,-0.007,-0.056,-0.007,-0.09 v-0.017 c0,-0.034,0.002,-0.064,0.007,-0.09 c0.005,-0.026,0.012,-0.047,0.02,-0.068 c0.008,-0.017,0.018,-0.03,0.028,-0.043 c0.012,-0.009,0.022,-0.013,0.035,-0.013 s0.023,0.004,0.035,0.013 c0.012,0.009,0.02,0.021,0.028,0.043 c0.008,0.017,0.015,0.038,0.02,0.068 c0.005,0.026,0.008,0.056,0.008,0.09 v0.017 M0.705,0.538 h-0.025 c-0.007,0,-0.01,0,-0.015,0.004 c-0.003,0,-0.007,0.009,-0.008,0.017 c-0.002,0.009,-0.003,0.017,-0.005,0.026 c0,0.009,0,0.021,0,0.034 v0.197 h-0.06 v-0.419 h0.045 l0.01,0.094 h0 c0,-0.017,0,-0.03,0.003,-0.043 c0.002,-0.013,0.005,-0.021,0.008,-0.03 c0.003,-0.009,0.008,-0.013,0.012,-0.017 c0.005,0,0.01,-0.004,0.015,-0.004 s0.007,0,0.01,0 s0.007,0,0.008,0.004 v0.137 M0.884,0.615 c0,0.009,0,0.017,0,0.021 h-0.111 c0,0.021,0.003,0.043,0.008,0.056 s0.013,0.021,0.022,0.021 s0.01,0,0.015,-0.009 c0.005,-0.004,0.008,-0.017,0.01,-0.03 h0.053 c-0.002,0.026,-0.005,0.047,-0.01,0.064 h0 c-0.005,0.017,-0.012,0.034,-0.018,0.047 c-0.007,0.013,-0.015,0.021,-0.025,0.03 c-0.008,0.004,-0.018,0.009,-0.027,0.009 s-0.023,-0.004,-0.033,-0.013 s-0.02,-0.021,-0.028,-0.043 c-0.008,-0.017,-0.015,-0.038,-0.02,-0.068 c-0.005,-0.026,-0.007,-0.056,-0.007,-0.094 v-0.017 c0,-0.034,0.002,-0.064,0.007,-0.09 c0.005,-0.026,0.012,-0.047,0.018,-0.068 c0.008,-0.017,0.017,-0.034,0.028,-0.043 c0.01,-0.009,0.022,-0.013,0.033,-0.013 s0.02,0,0.03,0.013 c0.01,0.009,0.02,0.021,0.027,0.034 c0.008,0.017,0.015,0.038,0.02,0.064 c0.005,0.026,0.008,0.06,0.008,0.094 v0.021" />
            </clipPath>
          </defs>
        </svg>
      </div>
      <div className="z-10 flex w-full flex-col">
        <div className="flex flex-col justify-between gap-20 border-t border-white/15 px-3 py-10 md:px-4 lg:flex-row">
          <div className="flex flex-col items-start gap-6">
            <Link href="/" target="_self">
              <MoreLogoImage className="h-5 fill-white" />
            </Link>
            <Popover
              onOpenChange={(open) => {
                if (open) copyToClipboard(emailAddress);
              }}
            >
              <PopoverTrigger className="-mt-1 -ml-2 w-fit px-2 py-1 typography-para-30 text-white transition-all duration-300 hover:rounded-lg hover:bg-white/10 lg:typography-para-20">
                {emailAddress}
              </PopoverTrigger>
              <PopoverContent
                className="flex items-center gap-2 rounded-md bg-orange-60 px-2 py-1 typography-para-20 text-white shadow-2xl shadow-neutral-110"
                side="right"
                sideOffset={8}
                hideWhenDetached
              >
                <ContentCopyIcon className="size-4 fill-white" />
                Copied!
              </PopoverContent>
            </Popover>
            <div className="flex gap-5">
              {socials.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  className="flex size-6 items-center justify-center [&_svg]:fill-white"
                >
                  <social.icon />
                </a>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-10 lg:flex-row lg:gap-15">
            <div className="flex flex-col gap-4">
              <Link
                className="-mt-1 -ml-2 flex w-fit items-center gap-2 px-2 py-1 typography-para-30 text-white transition-all duration-300 hover:rounded-lg hover:bg-white/10 lg:typography-para-20"
                href="/login"
                target="_blank"
              >
                Orders
                <ArrowOutwardIcon className="size-4 fill-white" />
              </Link>
            </div>
            <div className="flex flex-col gap-4">
              {featuredLinks.map((link) => (
                <Link
                  className="-mt-1 -ml-2 w-fit px-2 py-1 typography-para-30 text-white transition-all duration-300 hover:rounded-lg hover:bg-white/10 lg:typography-para-20"
                  key={link.name}
                  href={link.href}
                  target={link.target || "_self"}
                >
                  {link.name}
                </Link>
              ))}
            </div>
            <div className="flex flex-col gap-4">
              <h1 className="typography-para-30 text-white/75 lg:hidden">
                App
              </h1>
              <a
                className="-mt-1 -ml-1 w-fit px-1 py-1 typography-para-20 text-white transition-all duration-300 hover:rounded-lg hover:bg-white/10"
                href="https://play.google.com/store/apps/details?id=in.moreretail.grocery"
                target="_blank"
              >
                <PlayStoreBadgeImage />
              </a>
              <a
                className="-mt-1 -ml-1 w-fit px-1 py-1 typography-para-20 text-white transition-all duration-300 hover:rounded-lg hover:bg-white/10"
                href="https://apps.apple.com/in/app/more-grocery-delivery/id6444301593"
                target="_blank"
              >
                <AppStoreBadgeImage />
              </a>
            </div>
            <div className="flex flex-col gap-4">
              <h1 className="typography-para-30 text-white/75 lg:hidden">
                About
              </h1>
              {aboutLinks.map((link) => (
                <Link
                  className="pointer-events-auto z-50 -mt-1 -ml-2 w-fit px-2 py-1 typography-para-30 text-white transition-all duration-300 hover:rounded-lg hover:bg-white/10 lg:typography-para-20"
                  key={link.name}
                  href={link.href}
                  target={link.target || "_self"}
                >
                  {link.name}
                </Link>
              ))}
            </div>
            <div className="flex flex-col gap-4">
              {otherLinks.map((link) => (
                <Link
                  className="-mt-1 -ml-2 w-fit px-2 py-1 typography-para-30 text-white transition-all duration-300 hover:rounded-lg hover:bg-white/10 lg:typography-para-20"
                  key={link.name}
                  href={link.href}
                  target={link.target || "_self"}
                >
                  {link.name}
                </Link>
              ))}
            </div>
            <div className="flex flex-col gap-4">
              {legalLinks.map((link) => (
                <Link
                  className="-mt-1 -ml-2 w-fit px-2 py-1 typography-para-30 text-white transition-all duration-300 hover:rounded-lg hover:bg-white/10 lg:typography-para-20"
                  key={link.name}
                  href={link.href}
                  target={link.target || "_self"}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
        <div className="flex w-full items-center justify-between border-t border-white/15 px-3 py-10 md:px-4">
          <p className="typography-para-40 text-white lg:typography-para-30">
            <span>more retail private limited</span>
            &nbsp;©&nbsp;2025
          </p>
          <p className="typography-para-40 text-white lg:typography-para-30">
            your supermarket
          </p>
        </div>
      </div>
      {/* <div className="inverted-border-radius absolute top-0 z-20 h-8 w-full rounded-b-3xl bg-white" /> */}
      {background.type === "color" && (
        <div
          className="absolute inset-0 z-0 size-full"
          style={{
            backgroundColor: background.hex
              ? background.hex
              : tokens.color.orange[60],
          }}
        />
      )}
      {background.type === "video" && background.src && (
        <Video
          className="absolute inset-0 size-full object-cover"
          src={background.src}
          loop
          muted
          autoPlay
        />
      )}
      <div className="noise absolute inset-0 z-[1] size-full bg-[url(/images/common/noise.png)] bg-center bg-repeat opacity-5" />
    </footer>
  );
}

export { Footer };
